/**
 * WebSocket-Anbindung fuer Live-Updates (Spec 2 / Phase 1) und Presence
 * (Spec 7.1, Phase 5).
 *
 * Bewusst schlicht: ein Callback, ein Reconnect mit fester Wartezeit — und
 * **eine** Verbindung je Seite. Presence haengt deshalb als Option an
 * `useTicketEvents()` statt an einem zweiten Socket: der Kanal ist derselbe,
 * nur die Richtung kommt dazu.
 *
 * Callback und Presence-Absicht liegen in Refs — sonst wuerde jede neue
 * Render-Funktion die Verbindung neu aufbauen.
 */
import { PRESENCE_HEARTBEAT_MS, type RealtimeEvent } from '@tickethub/shared';
import { useEffect, useRef } from 'react';
import { API_BASE, apiPaths } from './api-paths';

const RECONNECT_DELAY_MS = 3000;

/**
 * Presence-Absicht dieser Seite (Spec 7.1). `editing` heisst „Antwort-Editor
 * offen" und ist das weiche Lock; `typing` setzt der Editor und es verfaellt
 * serverseitig von selbst.
 */
export type PresenceIntent = {
  ticketId: string;
  editing: boolean;
  typing: boolean;
};

function eventsUrl(tenantId: string): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}${API_BASE}${apiPaths.events(tenantId)}`;
}

/**
 * Abonniert die Ticket-Ereignisse des Mandanten.
 *
 * @param tenantId `null` = kein Mandant gewaehlt, es wird nichts geoeffnet.
 * @param onEvent  wird bei jedem Ereignis aufgerufen.
 * @param presence `null`/weggelassen = keine Presence-Meldung (Listen-Ansicht).
 */
export function useTicketEvents(
  tenantId: string | null,
  onEvent: (event: RealtimeEvent) => void,
  presence?: PresenceIntent | null,
): void {
  const handler = useRef(onEvent);
  const intent = useRef<PresenceIntent | null>(presence ?? null);
  const socketRef = useRef<WebSocket | null>(null);

  // Refs erst nach dem Render aktualisieren — waehrend des Renders ist der
  // Zugriff auf `.current` nicht erlaubt (react-hooks/refs).
  useEffect(() => {
    handler.current = onEvent;
  }, [onEvent]);

  const ticketId = presence?.ticketId ?? null;
  const editing = presence?.editing ?? false;
  const typing = presence?.typing ?? false;

  /** Presence-Signal — still verworfen, wenn die Verbindung gerade weg ist. */
  const signal = useRef((type: 'presence.join' | 'presence.ping' | 'presence.leave') => {
    const current = intent.current;
    const socket = socketRef.current;
    if (!current || socket?.readyState !== WebSocket.OPEN) return;
    socket.send(
      JSON.stringify({
        type,
        ticket_id: current.ticketId,
        editing: current.editing,
        typing: current.typing,
      }),
    );
  });

  useEffect(() => {
    if (!tenantId) return;

    // Der Sender liegt in einem Ref, damit er stabil bleibt; fuer die
    // Aufraeumfunktion wird er hier einmal festgehalten (react-hooks).
    const send = signal.current;
    let reconnect: ReturnType<typeof setTimeout> | null = null;
    let closed = false;

    const open = () => {
      if (closed) return;
      const socket = new WebSocket(eventsUrl(tenantId));
      socketRef.current = socket;

      socket.onopen = () => {
        send('presence.join');
      };

      socket.onmessage = (message) => {
        try {
          handler.current(JSON.parse(String(message.data)) as RealtimeEvent);
        } catch {
          // Unlesbare Nachricht: ignorieren statt die Verbindung zu verlieren.
        }
      };

      socket.onclose = () => {
        if (closed) return;
        reconnect = setTimeout(open, RECONNECT_DELAY_MS);
      };
    };

    open();
    // Lebenszeichen deutlich unter dem Server-Timeout (Spec 7.1).
    const heartbeat = setInterval(() => send('presence.ping'), PRESENCE_HEARTBEAT_MS);

    return () => {
      send('presence.leave');
      closed = true;
      if (reconnect) clearTimeout(reconnect);
      clearInterval(heartbeat);
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, [tenantId]);

  /**
   * Neue Absicht (Editor auf, jemand tippt) geht **sofort** raus — sonst
   * haengt das Kollisions-Banner der Wirklichkeit einen Herzschlag hinterher.
   */
  useEffect(() => {
    const next = ticketId === null ? null : { ticketId, editing, typing };
    const previous = intent.current;
    intent.current = next;

    if (next === null) {
      if (previous !== null) {
        socketRef.current?.send(
          JSON.stringify({ type: 'presence.leave', ticket_id: previous.ticketId }),
        );
      }
      return;
    }
    signal.current('presence.join');
  }, [ticketId, editing, typing]);
}
